import { ChangeEvent, useContext, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import UploadFileRoundedIcon from "@mui/icons-material/UploadFileRounded";
import AutoFixHighRoundedIcon from "@mui/icons-material/AutoFixHighRounded";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import TimelineRoundedIcon from "@mui/icons-material/TimelineRounded";
import DataContext from "../../../context/DataContext";
import PaginatorContext from "../../../context/PaginatorContext";
import {
  useFetch,
  usePermissions,
} from "../../../hooks";
import {
  ClientProps,
  ToolsRunLineItemProps,
} from "../../../interfaces/interfaces";
import { ToolsRunStatusBadge } from "../../../components/documents/toolsRun/ToolsRunStatusBadge";
import { ToolsRunUploadForm } from "../../../components/documents/toolsRun/ToolsRunUploadForm";
import { ToolsRunResolveDialog } from "../../../components/documents/toolsRun/ToolsRunResolveDialog";
import { ToolsRunTimelineDrawer } from "../../../components/documents/toolsRun/ToolsRunTimelineDrawer";
import { formatToolsRunQuantity } from "../../../components/documents/toolsRun/toolsRunFormatters";
import { DocumentsTopNavbar } from "../../../components/documents/DocumentsTopNavbar";

interface TrackerResponse {
  success?: boolean;
  succes?: boolean;
  data: ToolsRunLineItemProps[];
}

interface Props {
  clientMode?: boolean;
  assignedClientOptions?: ClientProps[];
  assignedClientId?: number | null;
  onAssignedClientChange?: (clientId: number | null) => void;
}

const formatDate = (value: string | null) => {
  if (!value) return "—";
  const [year, month, day] = value.slice(0, 10).split("-");
  return `${month}/${day}/${year}`;
};

const reasonLabel = (value: string | null) =>
  value
    ? value
        .split(",")
        .map((reason) => reason.replaceAll("_", " "))
        .join(" · ")
    : "";

const filterLabelSx = {
  display: "block",
  mb: "5px",
  color: "#9CA1AB",
  fontSize: 10.5,
  fontWeight: 600,
  letterSpacing: ".6px",
  lineHeight: 1.2,
  textTransform: "uppercase",
};

const documentFilterSx = {
  "& .MuiOutlinedInput-root": {
    minHeight: 38,
    bgcolor: "#FAFAF8",
    borderRadius: "9px",
    fontSize: 13,
    "& fieldset": { borderColor: "#E2E2DD" },
    "&:hover fieldset": { borderColor: "#C9C7BE" },
    "&.Mui-focused fieldset": {
      borderColor: "#ED8B00",
      borderWidth: "1px",
    },
  },
  "& .MuiInputBase-input": {
    py: "8px",
  },
};

export const ToolsRunAdmin = ({
  clientMode = false,
  assignedClientOptions = [],
  assignedClientId = null,
  onAssignedClientChange,
}: Props) => {
  const { paginationModel, fetchPaginationModel } =
    useContext(PaginatorContext);
  const {
    clientOptions: globalClientOptions,
    wellOptions,
    fetchDataClient,
    fetchDataWells,
    fetchDataProducts,
  } = useContext(DataContext);
  const clientOptions = clientMode
    ? assignedClientOptions
    : globalClientOptions;
  const { canManageToolsRun } = usePermissions();
  const [clientId, setClientId] = useState("");
  const [wellId, setWellId] = useState("");
  const [invoice, setInvoice] = useState("");
  const [productSearchDraft, setProductSearchDraft] = useState("");
  const [productSearch, setProductSearch] = useState("");
  const [status, setStatus] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [uploadOpen, setUploadOpen] = useState(false);
  const [resolveRow, setResolveRow] = useState<ToolsRunLineItemProps | null>(
    null,
  );
  const [timelineRow, setTimelineRow] = useState<ToolsRunLineItemProps | null>(
    null,
  );

  useEffect(() => {
    if (!clientMode) {
      fetchDataClient();
      fetchDataProducts();
    }
    // DataContext actions are not referentially stable; master data only needs loading on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientMode]);

  useEffect(() => {
    if (!clientMode || !assignedClientId) return;
    const selectedId = String(assignedClientId);
    setClientId(selectedId);
    setWellId("");
    fetchDataWells(assignedClientId);
    // DataContext actions are not referentially stable; rerun only when assigned scope changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientMode, assignedClientId]);

  useEffect(() => {
    const timeout = window.setTimeout(
      () => setProductSearch(productSearchDraft.trim()),
      300,
    );
    return () => window.clearTimeout(timeout);
  }, [productSearchDraft]);

  useEffect(() => {
    if (!clientId || !wellId) setTimelineRow(null);
  }, [clientId, wellId]);

  const filters = useMemo(
    () => ({
      clientId,
      wellId,
      invoice,
      productSearch,
      status,
      dateFrom,
      dateTo,
    }),
    [clientId, wellId, invoice, productSearch, status, dateFrom, dateTo],
  );
  const { data, isLoading, error, fetchData, pagination } =
    useFetch<TrackerResponse>(
      "tools-run-paginate",
      paginationModel,
      undefined,
      undefined,
      filters,
    );
  const rows = data?.data || [];

  const resetPage = () => fetchPaginationModel({ ...paginationModel, page: 0 });
  const chooseClient = (value: string) => {
    const nextClientId =
      clientMode && !value && assignedClientId
        ? String(assignedClientId)
        : value;
    setClientId(nextClientId);
    setWellId("");
    if (clientMode) {
      onAssignedClientChange?.(nextClientId ? Number(nextClientId) : null);
    }
    if (nextClientId) fetchDataWells(Number(nextClientId));
    resetPage();
  };
  const filterText =
    (setter: (value: string) => void) =>
    (event: ChangeEvent<HTMLInputElement>) => {
      setter(event.target.value);
      resetPage();
    };
  const hasActiveFilters = Boolean(
    wellId || invoice || productSearchDraft || status || dateFrom || dateTo,
  );
  const clearFilters = () => {
    setWellId("");
    setInvoice("");
    setProductSearchDraft("");
    setProductSearch("");
    setStatus("");
    setDateFrom("");
    setDateTo("");
    setTimelineRow(null);
    resetPage();
  };

  return (
    <Box
      sx={{
        mt: "-80px",
        minHeight: "100vh",
        bgcolor: "#F3F3F0",
        color: "#29313D",
      }}
    >
      <DocumentsTopNavbar
        title="Tools Tracker"
        actions={
          canManageToolsRun ? (
            <Button
              variant="outlined"
              startIcon={<UploadFileRoundedIcon />}
              onClick={() => setUploadOpen(true)}
              sx={{
                borderColor: "#E7E7E3",
                color: "#2A2F38",
                borderRadius: "9px",
                px: "15px",
                py: "8px",
                fontSize: 13,
                fontWeight: 600,
                textTransform: "none",
                "&:hover": { borderColor: "#ED8B00", color: "#ED8B00" },
              }}
            >
              Import Excel
            </Button>
          ) : undefined
        }
      />

      <Box sx={{ maxWidth: 1500, mx: "auto", p: { xs: 2, md: 3.5 } }}>
        <Paper
          elevation={0}
          sx={{
            p: "16px 20px",
            mb: 2,
            border: "1px solid #E7E7E3",
            borderRadius: "14px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-end",
              gap: "11px",
              flexWrap: "wrap",
            }}
          >
            <Box sx={{ width: { xs: "100%", sm: 190 } }}>
              <Box component="label" sx={filterLabelSx}>
                Client
              </Box>
              <Autocomplete
                size="small"
                options={clientOptions}
                value={
                  clientOptions.find(
                    (client) => String(client.id) === clientId,
                  ) || null
                }
                getOptionLabel={(client) => client.name}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                onChange={(_, client) =>
                  chooseClient(client ? String(client.id) : "")
                }
                disableClearable={clientMode && clientOptions.length === 1}
                disabled={clientMode && clientOptions.length === 1}
                autoHighlight
                openOnFocus
                noOptionsText="No clients found"
                sx={documentFilterSx}
                renderInput={(params) => (
                  <TextField {...params} placeholder="Search clients…" />
                )}
              />
            </Box>
            <Box sx={{ width: { xs: "100%", sm: 190 } }}>
              <Box component="label" sx={filterLabelSx}>
                Well
              </Box>
              <Autocomplete
                size="small"
                options={wellOptions}
                value={
                  wellOptions.find((well) => String(well.id) === wellId) || null
                }
                getOptionLabel={(well) => well.name}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                onChange={(_, well) => {
                  setWellId(well?.id == null ? "" : String(well.id));
                  resetPage();
                }}
                disabled={!clientId}
                autoHighlight
                openOnFocus
                noOptionsText="No wells found"
                sx={documentFilterSx}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={clientId ? "Search wells…" : "Select a client"}
                  />
                )}
              />
            </Box>
            <Box sx={{ flex: "1 1 230px", minWidth: 210 }}>
              <Box component="label" sx={filterLabelSx}>
                Tool
              </Box>
              <TextField
                fullWidth
                size="small"
                value={productSearchDraft}
                onChange={filterText(setProductSearchDraft)}
                placeholder="Search tool or part number…"
                sx={documentFilterSx}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchRoundedIcon
                        sx={{ color: "#A6ABB5", fontSize: 18 }}
                      />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            <Box sx={{ width: { xs: "100%", sm: 145 } }}>
              <Box component="label" sx={filterLabelSx}>
                Invoice
              </Box>
              <TextField
                fullWidth
                size="small"
                value={invoice}
                onChange={filterText(setInvoice)}
                placeholder="Invoice…"
                sx={documentFilterSx}
              />
            </Box>
            {!clientMode ? (
              <Box sx={{ width: { xs: "100%", sm: 145 } }}>
                <Box component="label" sx={filterLabelSx}>
                  Status
                </Box>
                <FormControl fullWidth size="small" sx={documentFilterSx}>
                  <Select
                    value={status}
                    displayEmpty
                    inputProps={{ "aria-label": "Status" }}
                    onChange={(event) => {
                      setStatus(event.target.value);
                      resetPage();
                    }}
                  >
                    <MenuItem value="">All statuses</MenuItem>
                    <MenuItem value="matched">Matched</MenuItem>
                    <MenuItem value="needs_review">Needs review</MenuItem>
                    <MenuItem value="error">Error</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            ) : null}
            <Box sx={{ width: { xs: "100%", sm: 145 } }}>
              <Box component="label" sx={filterLabelSx}>
                From
              </Box>
              <TextField
                fullWidth
                size="small"
                type="date"
                value={dateFrom}
                onChange={filterText(setDateFrom)}
                sx={documentFilterSx}
                inputProps={{ "aria-label": "From date" }}
              />
            </Box>
            <Box sx={{ width: { xs: "100%", sm: 145 } }}>
              <Box component="label" sx={filterLabelSx}>
                To
              </Box>
              <TextField
                fullWidth
                size="small"
                type="date"
                value={dateTo}
                onChange={filterText(setDateTo)}
                sx={documentFilterSx}
                inputProps={{ "aria-label": "To date" }}
              />
            </Box>
            {hasActiveFilters ? (
              <Box
                component="button"
                onClick={clearFilters}
                sx={{
                  p: "8px 13px",
                  border: "1px solid #E2E2DD",
                  background: "#fff",
                  borderRadius: "9px",
                  fontSize: 12.5,
                  fontWeight: 600,
                  color: "#6B7280",
                  cursor: "pointer",
                  fontFamily: "inherit",
                  "&:hover": { borderColor: "#ED8B00", color: "#ED8B00" },
                }}
              >
                Clear filters
              </Box>
            ) : null}
          </Box>
        </Paper>

        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            Tools Run records could not be loaded.
          </Alert>
        ) : null}
        <Paper
          elevation={0}
          sx={{
            border: "1px solid #E4E3DC",
            borderRadius: 2.5,
            overflow: "hidden",
          }}
        >
          <TableContainer>
            <Table sx={{ minWidth: 1060 }}>
              <TableHead>
                <TableRow sx={{ bgcolor: "#F8F8F5" }}>
                  {[
                    "Date",
                    "Invoice",
                    "Client / well",
                    "Tool",
                    "Qty",
                    "Status",
                    "",
                  ].map((label) => (
                    <TableCell
                      key={label}
                      sx={{
                        color: "#707782",
                        fontSize: 11,
                        letterSpacing: 0.7,
                        textTransform: "uppercase",
                        fontWeight: 850,
                        borderColor: "#EAE9E3",
                      }}
                    >
                      {label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                      <CircularProgress size={30} />
                    </TableCell>
                  </TableRow>
                ) : null}
                {!isLoading && !rows.length ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                      <Inventory2OutlinedIcon
                        sx={{ fontSize: 36, color: "#B8BDC5", mb: 1 }}
                      />
                      <Typography fontWeight={750}>
                        No shipment rows found
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {clientMode
                          ? "Adjust the filters to find tool activity."
                          : "Adjust the filters or import an Excel workbook."}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : null}
                {!isLoading &&
                  rows.map((row) => (
                    <TableRow
                      key={row.id}
                      hover
                      sx={{ "& td": { borderColor: "#EFEEE9" } }}
                    >
                      <TableCell sx={{ whiteSpace: "nowrap", fontWeight: 650 }}>
                        {formatDate(row.date)}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={800}>
                          {row.invoice || "—"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={750}>
                          {row.client?.name || row.rawShipToClient || "—"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {row.well?.name || row.rawShipToWell || "—"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={750}>
                          {row.product?.name || row.toolNameRaw || "—"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {row.product?.partNumber || row.partNumber || "—"}
                        </Typography>
                      </TableCell>
                      <TableCell
                        sx={{
                          fontVariantNumeric: "tabular-nums",
                          fontWeight: 750,
                        }}
                      >
                        {formatToolsRunQuantity(row.qty ?? row.qtyRaw)}
                      </TableCell>
                      <TableCell>
                        <ToolsRunStatusBadge status={row.status} />
                        {row.status !== "matched" ? (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              display: "block",
                              mt: 0.65,
                              textTransform: "capitalize",
                              maxWidth: 190,
                            }}
                          >
                            {reasonLabel(row.errorReason || row.reviewReason)}
                          </Typography>
                        ) : null}
                      </TableCell>
                      <TableCell align="right">
                        <Stack
                          direction="row"
                          spacing={0.5}
                          justifyContent="flex-end"
                        >
                          {clientId &&
                          wellId &&
                          String(row.clientId) === clientId &&
                          String(row.wellId) === wellId &&
                          row.productId ? (
                            <Button
                              size="small"
                              startIcon={<TimelineRoundedIcon />}
                              onClick={() => setTimelineRow(row)}
                              sx={{ color: "#44586A", fontWeight: 800 }}
                            >
                              Timeline
                            </Button>
                          ) : null}
                          {canManageToolsRun && row.status === "needs_review" ? (
                            <Button
                              size="small"
                              startIcon={<AutoFixHighRoundedIcon />}
                              onClick={() => setResolveRow(row)}
                              sx={{ color: "#A65F00", fontWeight: 800 }}
                            >
                              Resolve
                            </Button>
                          ) : null}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={Number(pagination.totalRecords || 0)}
            page={paginationModel.page}
            rowsPerPage={paginationModel.pageSize}
            rowsPerPageOptions={[10, 25, 50, 100]}
            onPageChange={(_, page) =>
              fetchPaginationModel({ ...paginationModel, page })
            }
            onRowsPerPageChange={(event) =>
              fetchPaginationModel({
                page: 0,
                pageSize: Number(event.target.value),
              })
            }
          />
        </Paper>
      </Box>

      <ToolsRunUploadForm
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        onUploaded={(result) => {
          const importedClientId = result.rows.find(
            (row) => row.clientId,
          )?.clientId;
          if (importedClientId) {
            setClientId(String(importedClientId));
            fetchDataWells(importedClientId);
          }
          setWellId("");
          setInvoice("");
          setProductSearchDraft("");
          setProductSearch("");
          setStatus("");
          setDateFrom("");
          setDateTo("");
          resetPage();
          fetchData();
          setResolveRow(
            result.rows.find((row) => row.status === "needs_review") || null,
          );
        }}
      />
      <ToolsRunResolveDialog
        row={resolveRow}
        onClose={() => setResolveRow(null)}
        onResolved={fetchData}
      />
      <ToolsRunTimelineDrawer
        row={timelineRow}
        onClose={() => setTimelineRow(null)}
      />
    </Box>
  );
};
